import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import API from '../api';
import ExcelJS from 'exceljs/dist/exceljs.min.js';
import { DetailModal as SalesDetailModal } from './SalesActivities';
import { usePermissions } from '../hooks/usePermissions';
import { logDownload, logPrint } from '../utils/eventLog';
import { printedByLabel } from '../utils/userLabel';
import { getKstDateString } from '../utils/dateKst';

const CAT_LIST  = ['회의','업무','발표','출장','교육','영업','납품','생산','케이스','기타'];

const CAT_COLOR = {
  '회의':'#1E3A8A','업무':'#00B050','발표':'#E8643A','출장':'#666699',
  '교육':'#00AAEE','영업':'#FF00FF','납품':'#E02020','생산':'#7C3AED',
  '케이스':'#71717A','기타':'#0D9488',
};
const CAT_BG = {
  '회의':'#D6E4FF','업무':'#DCFCE7','발표':'#FFECE5','출장':'#E6E6F5',
  '교육':'#E0F7FF','영업':'#FFE0FF','납품':'#FFE0E0','생산':'#EDE9FE',
  '케이스':'#F4F4F5','기타':'#CCFBF1',
};

const WEEKDAY = ['일','월','화','수','목','금','토'];

// 2026-08: 업무관리에서 자동등록된 일정처럼 계정관리의 커스텀 공개구분(예: "사내전체(생산,
// 품질 제외)")을 쓰는 경우, 상세보기 배지는 이미 visibility 문자열을 기준으로 정확히
// 잠금 여부를 판단하고 있었지만(아래 참고), 달력 칸/인쇄/엑셀 내보내기는 옛 is_public(0/1)
// 필드만 보고 있어서 두 곳의 판단이 어긋날 수 있었다(예: is_public이 갱신되지 않았거나
// 늦게 반영된 경우, 상세보기에는 🔒가 뜨는데 달력 칸에는 안 뜨는 문제). 모든 위치가 항상
// 같은 기준(visibility 우선, 없으면 is_public)으로 판단하도록 공용 함수로 통일한다.
const isRestricted = (s) => {
  if (s.visibility) return s.visibility !== '전체공개';
  return s.is_public !== 1;
};

// 생산관리에서 자동등록된 일정(schedules.source_plan_id) 앞에 붙일 아이콘 — 수동으로 만든
// 일정과 구분하고, 이벤트 종류(시작/종료/수량변경)를 글자 없이 한눈에 알아볼 수 있도록
// 종류별로 다른 아이콘을 붙인다. (백엔드에서 title을 모델명만 짧게 내려주므로, 여기서
// 아이콘이 "생산시작/생산종료/생산수량변경" 구분을 대신함)
const PROD_EVENT_ICON = { start: '🏭', end: '🏭', qty_change: '🔄🏭' };
const prodIcon = (s) => s && s.source_plan_id ? (PROD_EVENT_ICON[s.production_event] || '🏭') + ' ' : '';

// 연기 일수 — 최초 등록일(original_start_date, 백엔드가 연기이력 중 가장 오래된 항목의
// old_start_date로 내려줌)부터 현재 시작일까지 며칠이나 밀렸는지 계산한다. 달력에 "D+xx"
// 형태로 표시하는 데 사용한다.
// 연기 이력의 "옛 날짜" 전체 목록 — 백엔드가 postpone_history_dates에 변경 시각(changed_at)
// 오름차순으로 콤마 구분해 내려준다. 인덱스+1이 곧 "몇 차 연기였는지"(1차, 2차, ...)가 된다.
const historyDates = (s) => s.postpone_history_dates
  ? String(s.postpone_history_dates).split(',').map(d => d.trim()).filter(Boolean)
  : [];

// 지연(ghost) 표시 라벨 — 총 연기 횟수가 1회뿐이면 굳이 "1차"를 붙이지 않고 그냥 "연기"로,
// 2회 이상이면 몇 번째 연기였는지 알 수 있도록 "1차연기"/"2차연기"/... 로 표시한다.
const postponeLabel = (s, ordinal) => s.postpone_count === 1 ? '연기' : `${ordinal}차연기`;

const postponeDays = (s) => {
  if (!s.postpone_count || !s.original_start_date) return 0;
  const a = new Date(s.original_start_date);
  const b = new Date(s.start_date);
  if (isNaN(a) || isNaN(b)) return 0;
  return Math.round((b - a) / 86400000);
};

// 시작일~종료일이 다른(여러날 걸친) 일정을 달력에 표시할 때, 시작일 칸에는 "제목 (종료일 M/D)",
// 종료일 칸에는 "제목 (완료)"를 덧붙여 한눈에 언제 끝나는 일정인지 알 수 있게 한다.
// 하루짜리 일정이거나, 시작~종료 사이의 중간 날짜에는 아무것도 붙이지 않는다.
const spanLabel = (s, dateStr) => {
  if (!s.end_date || s.end_date === s.start_date) return '';
  if (dateStr === s.start_date) {
    const m = parseInt(s.end_date.slice(5, 7), 10);
    const d = parseInt(s.end_date.slice(8, 10), 10);
    return ` (${m}/${d})`;
  }
  if (dateStr === s.end_date) return ' (완료)';
  return '';
};

// 시작일도 종료일도 아닌, 여러날 일정의 "중간" 날짜인지 여부 — 이런 날에는 캘린더에
// 아예 표시하지 않는다(시작일/종료일 칸에만 제목이 나타나도록).
const isMidSpanDay = (s, dateStr) =>
  !!(s.end_date && s.end_date !== s.start_date && dateStr > s.start_date && dateStr < s.end_date);

const getToday = () => getKstDateString();

const formatDateTime = (str) => {
  if (!str) return '-';
  const dt = new Date(str.replace(' ','T'));
  if (isNaN(dt)) return str;
  const pad = n => String(n).padStart(2,'0');
  return dt.getFullYear()+'년 '+pad(dt.getMonth()+1)+'월 '+pad(dt.getDate())+'일 '+
    pad(dt.getHours())+':'+pad(dt.getMinutes());
};

function buildCalendar(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const lastDate = new Date(year, month+1, 0).getDate();
  const cells = [];
  for (let i=0; i<firstDay; i++) cells.push(null);
  for (let d=1; d<=lastDate; d++) cells.push(d);
  return cells;
}

export default function Schedules() {
  const [list, setList]               = useState([]);
  const [selected, setSelected]       = useState(null);
  const [showForm, setShowForm]       = useState(false);
  const [editData, setEditData]       = useState(null);
  const [formDate, setFormDate]       = useState('');
  const [viewMode, setViewMode]       = useState('calendar');
  const [msg, setMsg]                 = useState('');
  const [curYear, setCurYear]         = useState(new Date().getFullYear());
  const [curMonth, setCurMonth]       = useState(new Date().getMonth());
  const [showExport, setShowExport]   = useState(false);
  const [exportType, setExportType]   = useState('month');
  const [exportYear, setExportYear]   = useState(new Date().getFullYear());
  const [exportMonth, setExportMonth] = useState(new Date().getMonth()+1);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear());
  const datePickerRef = useRef(null);
  // 2026-09 요청: 하단 범주(회의/업무/발표...) 범례를 년월 표시줄 우측으로 이동 + 클릭시
  // 해당 범주만 필터링. null이면 전체보기.
  const [categoryFilter, setCategoryFilter] = useState(null);

  // ── 공휴일(대체공휴일 포함) 자동 조회 ── 관리자가 직접 입력하지 않고, 서버가
  // @hyunbinseo/holidays-kr(우주항공청 월력요항 기반) 데이터를 그대로 내려준다.
  // 화면에 표시 중인 연도(curYear)가 바뀔 때마다 그 해의 공휴일을 다시 불러온다.
  // ({ 'YYYY-MM-DD': ['공휴일명', ...] } 형태 — 인쇄(handlePrint)에서도 그대로 재사용)
  const [holidays, setHolidays] = useState({});
  useEffect(() => {
    API.get('/schedules/holidays?year=' + curYear).then(r => setHolidays(r.data || {})).catch(() => {});
  }, [curYear]);

  // 연/월 선택 팝오버: 열릴 때 현재 조회연도로 초기화 + 바깥 클릭 시 닫기
  useEffect(() => { if (showDatePicker) setPickerYear(curYear); }, [showDatePicker, curYear]);
  useEffect(() => {
    if (!showDatePicker) return;
    const onClickOutside = (e) => { if (datePickerRef.current && !datePickerRef.current.contains(e.target)) setShowDatePicker(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [showDatePicker]);

  const user    = JSON.parse(sessionStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role === 'superadmin' || user.role === 'admin';
  // 관리자/생산관리자/협력사 역할 개념이 없어졌으므로, 최고관리자 외에는 예외 없이
  // 계정관리 > 권한설정에서 부여한 일정관리 쓰기 권한을 그대로 따릅니다(예전에는 isAdmin으로
  // 무조건 막아서 쓰기 권한을 줘도 등록 버튼이 계속 안 보였음).
  const { canWrite: canWriteMenu } = usePermissions();
  const canWriteSchedules = canWriteMenu('schedules');

  // ── 영업관리/출고관리/생산관리 연동 (날짜별 아이콘 표시 + 통합 일정 목록) ──
  const [salesActivities, setSalesActivities] = useState([]);
  const [salesLogs, setSalesLogs]             = useState([]);
  const [shipments, setShipments]             = useState([]);
  const [productions, setProductions]         = useState([]);
  const [dayPicker, setDayPicker]             = useState(null); // { dateStr, sales:[], shipments:[] } (뱃지 클릭시 간단 선택)
  const [dayList, setDayList]                 = useState(null); // { dateStr, items:[] } (날짜 칸 클릭시 통합 목록)
  const [salesDetailId, setSalesDetailId]     = useState(null);
  const [shipmentDetail, setShipmentDetail]   = useState(null);
  const [productionDetail, setProductionDetail] = useState(null);

  const loadSales = () => API.get('/sales-activities/export/all')
    .then(r => { setSalesActivities(r.data.activities||[]); setSalesLogs(r.data.logs||[]); }).catch(()=>{});
  const loadShipments = () => API.get('/shipments').then(r => setShipments(r.data)).catch(()=>{});
  const loadProductions = () => API.get('/productions/plans').then(r => setProductions(r.data)).catch(()=>{});

  useEffect(() => { loadSales(); loadShipments(); loadProductions(); }, []);

  // 새로고침 없이 새 내용이 자동으로 보이도록 15초마다 조용히 다시 불러옴
  useEffect(() => {
    const t = setInterval(() => { load(); loadSales(); loadShipments(); loadProductions(); }, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  // 등록/수정/삭제 완료 메시지가 계속 남아있던 버그 수정: 일정 시간 후 자동으로 사라지게 함
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(() => setMsg(''), 3000);
    return () => clearTimeout(t);
  }, [msg]);

  // 시간(HH:MM) 추출: '2026. 07. 30. 14:23:11' 또는 'YYYY-MM-DD HH:MM:SS' 등 다양한 포맷 대응
  const extractTime = (str) => {
    if (!str) return '';
    const m = str.match(/(\d{1,2}):(\d{2})/);
    return m ? `${m[1].padStart(2,'0')}:${m[2]}` : '';
  };

  // 날짜(YYYY-MM-DD) -> 해당일에 표시할 영업활동 목록 (등록 또는 영업사항 업데이트 기준)
  // 같은 활동이 하루에 여러 번 업데이트되어도 1건만 표시 (가장 최근 업데이트 시간 기준)
  const salesOnDate = (dateStr) => {
    const map = new Map(); // activityId -> { id, time, partner, customer }

    salesActivities.forEach(a => {
      if ((a.activity_date||'').slice(0,10) === dateStr) {
        map.set(a.id, { id:a.id, time:'등록', partner:a.partner_company||'-', customer:a.customer_company||'-' });
      }
    });
    salesLogs.forEach(l => {
      if ((l.created_at||'').slice(0,10) === dateStr) {
        const act = salesActivities.find(a => a.id === l.activity_id);
        const time = extractTime(l.created_at);
        const prev = map.get(l.activity_id);
        if (!prev || prev.time === '등록' || time > prev.time) {
          map.set(l.activity_id, {
            id: l.activity_id, time,
            partner: act?.partner_company || '-',
            customer: act?.customer_company || '-',
          });
        }
      }
    });
    return Array.from(map.values());
  };

  // 날짜(YYYY-MM-DD) -> 해당일 출고 목록 (출고일 기준)
  const shipmentsOnDate = (dateStr) =>
    shipments.filter(s => (s.ship_date||'').slice(0,10) === dateStr);

  // 날짜(YYYY-MM-DD) -> 해당일이 생산계획의 시작일 또는 종료일인 경우만 (생산 아이콘은
  // 시작일/종료일에만 표시. 생산수량 변경 시점은 생산승인 시 schedules 테이블에 별도
  // 일정(🏭 접두어)으로 이미 등록되므로 여기서 별도로 다룰 필요가 없음. 예전에는 시작일~
  // 종료일 사이 매일 표시해서, 일정관리에 이미 등록된 생산 일정(🏭 시작/종료 항목)과
  // 중복으로 표시되는 문제가 있었음)
  const productionsOnDate = (dateStr) =>
    productions.filter(p => p.start_date && (p.start_date === dateStr || (p.end_date || p.start_date) === dateStr));

  const load = () => API.get('/schedules').then(r => setList(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);

  // 화면 하단 "최신글/업데이트" 활동피드 바에서 일정관리 항목을 클릭해 들어온 경우, 그 항목의
  // 상세 모달을 바로 연다. list가 아직 로드되기 전(마운트 직후)일 수 있으므로 list가 채워질
  // 때까지 대기했다가 찾아서 연다.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const ao = location.state?.activityOpen;
    if (ao && list.length) {
      const found = list.find(s => String(s.id) === String(ao.itemId));
      if (found) setSelected(found);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, list]); // eslint-disable-line

  const handleDelete = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    await API.delete('/schedules/'+id);
    setSelected(null); setMsg('삭제 완료!'); load();
  };

  const schedulesOnDate = dateStr =>
    list.filter(s => s.start_date <= dateStr && (s.end_date||s.start_date) >= dateStr
      && (!categoryFilter || s.category === categoryFilter));

  const cells = buildCalendar(curYear, curMonth);
  const today = getToday();

  const thisMonthList = list.filter(s => {
    const ym = curYear+'-'+String(curMonth+1).padStart(2,'0');
    return s.start_date.startsWith(ym) || (s.end_date||'').startsWith(ym);
  });

  // ── 인쇄
  const handlePrint = () => {
    const heads = WEEKDAY.map((d,i) =>
      `<div class="cal-head" style="color:${i===0?'#dc2626':i===6?'#2563eb':'#444'}">${d}</div>`
    ).join('');
    const c = buildCalendar(curYear, curMonth);
    const cellsHtml = c.map((day) => {
      if (!day) return `<div class="cal-cell" style="background:#fafafa"></div>`;
      const dateStr   = curYear+'-'+String(curMonth+1).padStart(2,'0')+'-'+String(day).padStart(2,'0');
      const holidayNames = holidays[dateStr];
      const dayScheds = schedulesOnDate(dateStr);
      // 화면 달력과 동일하게, 생산관리에서 자동등록된 일정(source_plan_id 있음)은 글자 목록이
      // 아니라 우측 상단 아이콘 뱃지로만 표시한다.
      const dayProdScheds    = dayScheds.filter(s => s.source_plan_id);
      // 여러날 일정의 "중간" 날짜(시작일도 종료일도 아닌 날)는 캘린더에 표시하지 않는다.
      const dayVisibleScheds = dayScheds.filter(s => !s.source_plan_id && !isMidSpanDay(s, dateStr));
      const dayGhostScheds = [];
      list.forEach(s => {
        if (s.source_plan_id || !s.postpone_count) return;
        historyDates(s).forEach((d, idx) => {
          if (d === dateStr && d !== s.start_date) dayGhostScheds.push({ s, ordinal: idx+1 });
        });
      });
      const daySales       = salesOnDate(dateStr);
      const dayShipments   = shipmentsOnDate(dateStr);
      const isToday   = dateStr === today;
      const dow       = new Date(dateStr).getDay();
      // 휴무일(대체공휴일 포함) 색상은 일요일과 동일한 빨간색(#dc2626)을 그대로 사용
      const dayColor  = holidayNames ? '#dc2626' : dow===0?'#dc2626':dow===6?'#2563eb':'#333';
      const holidayLabel = holidayNames ? `<span style="font-size:11px;color:#dc2626;font-weight:600;">(${holidayNames[0]})</span>` : '';
      // 날짜 숫자 + 휴무일명을 하나의 flex 아이템으로 묶어서, 우측의 아이콘 뱃지와
      // justify-content:space-between으로 배치되어도 휴무일명이 날짜 숫자 바로 옆에 붙어있게 함
      // (묶지 않으면 날짜와 휴무일명이 각각 별도 flex 아이템이 되어 뱃지 쪽으로 밀려남)
      const dayHtml   = isToday
        ? `<span style="display:inline-flex;align-items:center;gap:3px;"><span style="background:#185FA5;color:#fff;border-radius:50%;width:20px;height:20px;display:inline-flex;align-items:center;justify-content:center;">${day}</span>${holidayLabel}</span>`
        : `<span style="display:inline-flex;align-items:center;gap:3px;"><span style="color:${dayColor};font-weight:400">${day}</span>${holidayLabel}</span>`;
      // 화면에 표시되는 일자별 아이콘(💼 영업활동 / 🚚 출고 / 🏭 생산)을 인쇄물에도 그대로 반영
      const badges = [];
      if (daySales.length)     badges.push(`<span style="font-size:9px;color:#7C3AED;">💼${daySales.length>1?daySales.length:''}</span>`);
      if (dayShipments.length) badges.push(`<span style="font-size:9px;color:#185FA5;">🚚${dayShipments.length>1?dayShipments.length:''}</span>`);
      if (dayProdScheds.length) {
        const icon = dayProdScheds.length===1 ? (PROD_EVENT_ICON[dayProdScheds[0].production_event]||'🏭') : '🏭';
        badges.push(`<span style="font-size:9px;color:#5B21B6;">${icon}${dayProdScheds.length>1?dayProdScheds.length:''}</span>`);
      }
      const badgeHtml = badges.length ? `<span style="display:inline-flex;gap:3px;">${badges.join('')}</span>` : '';
      const eventsHtml = dayVisibleScheds.slice(0,3).map(s => {
        // 인쇄물에서는 배경색만 범주를 구분하는 용도로 남기고, 입력된 일정 제목(내용)의
        // 글자색은 항상 검정으로 고정한다(예전에는 흰 글자였는데, 진한 배경이 아니거나
        // 흑백으로 인쇄될 때 잘 안 보인다는 피드백을 반영). 배경은 진한 CAT_COLOR 대신
        // 연한 CAT_BG를 써서 검정 글자와의 대비를 확보한다.
        const bg = CAT_BG[s.category]||'#f0f0f0';
        const dplusHtml = (s.postpone_count>0 && dateStr===s.start_date) ? ` (D+${postponeDays(s)})` : '';
        const spanHtml = spanLabel(s, dateStr);
        return `<div style="font-size:10px;padding:1px 4px;border-radius:3px;margin-bottom:2px;background:${bg};color:#000;font-weight:400;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${s.completed?'✓ ':''}${isRestricted(s)?'🔒 ':''}${s.title}${spanHtml}${dplusHtml}</div>`;
      }).join('');
      const ghostHtml = dayGhostScheds.map(({ s, ordinal }) =>
        `<div style="font-size:10px;padding:1px 4px;border-radius:3px;margin-bottom:2px;background:#fff;border:1px dashed #bbb;color:#999;font-weight:400;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${s.title} (${postponeLabel(s, ordinal)})</div>`
      ).join('');
      const moreCount = dayVisibleScheds.length - 3;
      const moreHtml = moreCount>0?`<div style="font-size:9px;color:#888">+${moreCount}개</div>`:'';
      return `<div class="cal-cell"><div style="font-size:12px;margin-bottom:3px;display:flex;justify-content:space-between;align-items:center;">${dayHtml}${badgeHtml}</div>${eventsHtml}${ghostHtml}${moreHtml}</div>`;
    }).join('');
    // 하단 범주(회의/업무/발표 등) 범례는 인쇄내용에서 제외 요청에 따라 더 이상 만들지 않음
    const now = new Date();
    const printDT = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    const printBy = printedByLabel(user); // 출력자: 부서 직급 이름 (최고관리자는 "최고관리자"로 표시)
    const footerHtml = `<div style="display:flex;justify-content:space-between;margin-top:8px;padding-top:8px;border-top:1px solid #ddd;font-size:11px;color:#555;flex:0 0 auto;">
        <span>출력일시: ${printDT}</span><span>출력자: ${printBy}</span>
      </div>`;
    // 달력이 페이지 세로 폭을 꽉 채우도록, 주(週) 행 개수를 계산해서 grid-template-rows를
    // 동일 비율(1fr)로 나눠준다. body를 flex column으로 만들고 .cal-grid에 flex:1을 줘서
    // 제목/달력/푸터가 페이지 전체 높이를 나눠 갖게 한다(예전에는 행 높이가 내용만큼만
    // 차지해서 세로로 짧게 인쇄되는 문제가 있었음).
    const weekCount = Math.ceil(c.length / 7);
    const pw = window.open('','_blank','width=1200,height=850');
    pw.document.write(`<!DOCTYPE html><html><head>
      <title>${curYear}년 ${curMonth+1}월 일정표</title>
      <style>@page{size:A4 landscape;margin:10mm}
      html,body{height:100%;margin:0}
      body{font-family:sans-serif;display:flex;flex-direction:column;height:100vh;box-sizing:border-box}
      h2{text-align:center;margin:0 0 10px;font-size:18px;flex:0 0 auto}
      .cal-grid{flex:1 1 auto;min-height:0;display:grid;grid-template-columns:repeat(7,1fr);
        grid-template-rows:auto repeat(${weekCount},1fr);border-top:1px solid #ddd;border-left:1px solid #ddd}
      .cal-head{padding:8px 0;text-align:center;font-size:12px;font-weight:600;background:#f8fafc;border-right:1px solid #ddd;border-bottom:1px solid #ddd}
      .cal-cell{padding:5px;border-right:1px solid #ddd;border-bottom:1px solid #ddd;overflow:hidden}</style>
      </head><body>
      <h2>${curYear}년 ${curMonth+1}월 일정표</h2>
      <div class="cal-grid">${heads}${cellsHtml}</div>
      ${footerHtml}
      </body></html>`);
    pw.document.close(); pw.focus();
    logPrint('일정관리', `${curYear}년 ${curMonth+1}월 일정표`);
    setTimeout(()=>{ pw.print(); pw.close(); }, 500);
  };

  const [exporting, setExporting] = useState(false);

  const pad2 = n => String(n).padStart(2, '0');
  const thinBorder = { style:'thin', color:{argb:'FFE5E7EB'} };

  // 한 달치 달력 형태의 시트를 워크북에 추가한다 (화면 캘린더/인쇄 PDF와 동일하게, 요일별
  // 7열 그리드에 날짜별 셀 하나씩. 공휴일(대체공휴일 포함)은 요청사항대로 "17 (대체휴일)"
  // 형태로 빨간색 표시하고, 각 날짜 칸 안에는 그 날의 일정 제목을 줄바꿈으로 나열한다)
  const addCalendarSheet = (wb, year, month1, holidaysMap, sheetName) => {
    const ws = wb.addWorksheet(sheetName);
    ws.columns = Array.from({ length:7 }, () => ({ width:23 }));

    ws.mergeCells(1,1,1,7);
    const title = ws.getCell(1,1);
    title.value = `${year}년 ${month1}월 일정표`;
    title.font = { size:16, bold:true, color:{argb:'FF1A1A1A'} };
    title.alignment = { horizontal:'center' };
    ws.getRow(1).height = 26;

    ws.mergeCells(2,1,2,7);
    const now = new Date();
    const sub = ws.getCell(2,1);
    sub.value = `기준일시: ${now.getFullYear()}-${pad2(now.getMonth()+1)}-${pad2(now.getDate())} ${pad2(now.getHours())}:${pad2(now.getMinutes())}`;
    sub.font = { size:10, italic:true, color:{argb:'FF888888'} };
    sub.alignment = { horizontal:'center' };

    const headerRowIdx = 3;
    WEEKDAY.forEach((d,i) => {
      const cell = ws.getCell(headerRowIdx, i+1);
      cell.value = d;
      cell.font = { bold:true, color:{argb: i===0?'FFDC2626':i===6?'FF2563EB':'FF444444'} };
      cell.alignment = { horizontal:'center', vertical:'middle' };
      cell.fill = { type:'pattern', pattern:'solid', fgColor:{argb:'FFF8FAFC'} };
      cell.border = { top:thinBorder, bottom:thinBorder, left:thinBorder, right:thinBorder };
    });
    ws.getRow(headerRowIdx).height = 20;

    const cells = buildCalendar(year, month1-1);
    let rowIdx = headerRowIdx + 1;
    for (let w=0; w<cells.length; w+=7) {
      const week = cells.slice(w, w+7);
      week.forEach((day, ci) => {
        const cell = ws.getCell(rowIdx, ci+1);
        cell.alignment = { horizontal:'left', vertical:'top', wrapText:true };
        cell.border = { top:thinBorder, bottom:thinBorder, left:thinBorder, right:thinBorder };
        if (!day) { cell.fill = { type:'pattern', pattern:'solid', fgColor:{argb:'FFFAFAFA'} }; return; }

        const dateStr = `${year}-${pad2(month1)}-${pad2(day)}`;
        const holidayNames = holidaysMap[dateStr];
        const dayColor = holidayNames ? 'FFDC2626' : ci===0 ? 'FFDC2626' : ci===6 ? 'FF2563EB' : 'FF333333';
        const dayScheds = list.filter(s => s.start_date <= dateStr && (s.end_date||s.start_date) >= dateStr);
        // 화면 달력/인쇄와 동일하게, 생산관리에서 자동등록된 일정(source_plan_id 있음)은 글자
        // 목록이 아니라 우측 아이콘 뱃지로만 표시한다.
        const dayProdScheds    = dayScheds.filter(s => s.source_plan_id);
        const dayVisibleScheds = dayScheds.filter(s => !s.source_plan_id && !isMidSpanDay(s, dateStr));
        const dayGhostScheds = [];
        list.forEach(s => {
          if (s.source_plan_id || !s.postpone_count) return;
          historyDates(s).forEach((d, idx) => {
            if (d === dateStr && d !== s.start_date) dayGhostScheds.push({ s, ordinal: idx+1 });
          });
        });
        const shown = dayVisibleScheds.slice(0, 8);
        // 화면/인쇄에 표시되는 일자별 아이콘(💼 영업활동 / 🚚 출고 / 🏭 생산)도 엑셀에 그대로 반영
        const daySales     = salesOnDate(dateStr);
        const dayShipments = shipmentsOnDate(dateStr);

        // 일자 숫자는 그대로(11px/굵게) 유지하고, 옆의 "(공휴일명)" 글자만 10px로 별도 서식
        const runs = [{ font:{ bold:true, color:{argb:dayColor}, size:11 }, text: `${day}` }];
        if (holidayNames) {
          runs.push({ font:{ bold:true, color:{argb:'FFDC2626'}, size:10 }, text: ` (${holidayNames[0]})` });
        }
        if (daySales.length) {
          runs.push({ font:{ size:9, color:{argb:'FF7C3AED'} },
            text: `  💼${daySales.length>1?daySales.length:''}` });
        }
        if (dayShipments.length) {
          runs.push({ font:{ size:9, color:{argb:'FF185FA5'} },
            text: `  🚚${dayShipments.length>1?dayShipments.length:''}` });
        }
        if (dayProdScheds.length) {
          const icon = dayProdScheds.length===1 ? (PROD_EVENT_ICON[dayProdScheds[0].production_event]||'🏭') : '🏭';
          runs.push({ font:{ size:9, color:{argb:'FF5B21B6'} },
            text: `  ${icon}${dayProdScheds.length>1?dayProdScheds.length:''}` });
        }
        runs.push({ font:{ size:9 }, text:'\n' });
        shown.forEach(s => {
          const dplus = (s.postpone_count>0 && dateStr===s.start_date) ? ` (D+${postponeDays(s)})` : '';
          const span = spanLabel(s, dateStr);
          runs.push({ font:{ size:9, color:{argb:'FF333333'} },
            text: `• ${s.completed?'✓ ':''}${isRestricted(s)?'🔒 ':''}${s.title}${span}${dplus}\n` });
        });
        dayGhostScheds.forEach(({ s, ordinal }) => {
          runs.push({ font:{ size:9, italic:true, color:{argb:'FF999999'} },
            text: `• ${s.title} (${postponeLabel(s, ordinal)})\n` });
        });
        if (dayVisibleScheds.length > shown.length) {
          runs.push({ font:{ size:9, italic:true, color:{argb:'FF888888'} },
            text: `+${dayVisibleScheds.length-shown.length}개` });
        }
        cell.value = { richText: runs };
      });
      ws.getRow(rowIdx).height = 100;
      rowIdx++;
    }
    return ws;
  };

  const downloadWorkbook = async (wb, filename) => {
    const buf  = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type:'application/octet-stream' });
    const url  = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    logDownload('일정관리', filename);
  };

  const fetchHolidaysFor = (year) =>
    API.get('/schedules/holidays?year=' + year).then(r => r.data || {}).catch(() => ({}));

  const exportMonthExcel = async (year, month) => {
    setExporting(true);
    try {
      const holidaysMap = await fetchHolidaysFor(year);
      const wb = new ExcelJS.Workbook();
      addCalendarSheet(wb, year, month, holidaysMap, `${year}년 ${month}월`);
      await downloadWorkbook(wb, `IMS_일정_${year}_${pad2(month)}.xlsx`);
      setShowExport(false);
    } finally {
      setExporting(false);
    }
  };

  const exportYearExcel = async (year) => {
    setExporting(true);
    try {
      const holidaysMap = await fetchHolidaysFor(year);
      const wb = new ExcelJS.Workbook();
      for (let m=1; m<=12; m++) addCalendarSheet(wb, year, m, holidaysMap, `${m}월`);
      await downloadWorkbook(wb, `IMS_일정_${year}년_연간.xlsx`);
      setShowExport(false);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ padding:24, maxWidth:1690 }}>
      {/* 헤더 */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <h2 style={{ fontSize:20, fontWeight:600, margin:0 }}>📅 일정관리</h2>
          <p style={{ fontSize:13, color:'#888', margin:'4px 0 0' }}>월별 일정을 등록하고 관리합니다.</p>
        </div>
        <div className="hdr-actions" style={{ display:'flex', gap:8, alignItems:'center' }}>
          {/* 캘린더/목록 전환 버튼 삭제 — 항상 캘린더 형태로만 표시 */}
          <button onClick={handlePrint}
            style={{ background:'#475569', color:'#fff', border:'none', borderRadius:8,
              padding:'10px 16px', fontSize:14, cursor:'pointer', fontWeight:600 }}>
            🖨️ 인쇄
          </button>
          <div style={{ position:'relative' }}>
            <button onClick={() => setShowExport(!showExport)}
              style={{ background:'#16a34a', color:'#fff', border:'none', borderRadius:8,
                padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer',
                display:'flex', alignItems:'center', gap:6 }}>
              📥 내보내기 ▾
            </button>
            {showExport && (
              <div style={{ position:'absolute', right:0, top:44, background:'#fff',
                border:'1px solid #e5e7eb', borderRadius:12, padding:20, width:280,
                boxShadow:'0 8px 24px rgba(0,0,0,0.12)', zIndex:100 }}>
                <div style={{ fontSize:14, fontWeight:600, marginBottom:14 }}>📥 내보내기 옵션</div>
                <div style={{ display:'flex', border:'1px solid #ddd', borderRadius:8, overflow:'hidden', marginBottom:16 }}>
                  {[{val:'month',label:'월별'},{val:'year',label:'연간'}].map(t=>(
                    <button key={t.val} onClick={() => setExportType(t.val)}
                      style={{ flex:1, padding:'7px 0', border:'none', cursor:'pointer', fontSize:13, fontWeight:500,
                        background:exportType===t.val?'#185FA5':'#fff',
                        color:exportType===t.val?'#fff':'#666' }}>
                      {t.label}
                    </button>
                  ))}
                </div>
                {exportType==='month' && (
                  <div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:14 }}>
                      <div>
                        <label style={{ fontSize:12, color:'#888', display:'block', marginBottom:4 }}>연도</label>
                        <select value={exportYear} onChange={e => setExportYear(Number(e.target.value))}
                          style={{ width:'100%', padding:'7px 8px', border:'1px solid #ddd', borderRadius:6, fontSize:13 }}>
                          {Array.from({length:10},(_,i)=>new Date().getFullYear()-3+i).map(y=>(
                            <option key={y} value={y}>{y}년</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize:12, color:'#888', display:'block', marginBottom:4 }}>월</label>
                        <select value={exportMonth} onChange={e => setExportMonth(Number(e.target.value))}
                          style={{ width:'100%', padding:'7px 8px', border:'1px solid #ddd', borderRadius:6, fontSize:13 }}>
                          {Array.from({length:12},(_,i)=>i+1).map(m=>(
                            <option key={m} value={m}>{m}월</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <button onClick={() => exportMonthExcel(exportYear, exportMonth)} disabled={exporting}
                      style={{ width:'100%', padding:'9px', background: exporting?'#94d3ac':'#16a34a', color:'#fff',
                        border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor: exporting?'default':'pointer' }}>
                      {exporting ? '⏳ 만드는 중...' : `📥 ${exportYear}년 ${exportMonth}월 엑셀 다운로드`}
                    </button>
                  </div>
                )}
                {exportType==='year' && (
                  <div>
                    <div style={{ marginBottom:14 }}>
                      <label style={{ fontSize:12, color:'#888', display:'block', marginBottom:4 }}>연도 선택</label>
                      <select value={exportYear} onChange={e => setExportYear(Number(e.target.value))}
                        style={{ width:'100%', padding:'7px 8px', border:'1px solid #ddd', borderRadius:6, fontSize:13 }}>
                        {Array.from({length:10},(_,i)=>new Date().getFullYear()-3+i).map(y=>(
                          <option key={y} value={y}>{y}년</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ fontSize:12, color:'#888', marginBottom:10, padding:'8px 10px', background:'#f8fafc', borderRadius:6 }}>
                      💡 1월~12월이 각각 별도 시트로 저장돼요
                    </div>
                    <button onClick={() => exportYearExcel(exportYear)} disabled={exporting}
                      style={{ width:'100%', padding:'9px', background: exporting?'#c7a172':'#854F0B', color:'#fff',
                        border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor: exporting?'default':'pointer' }}>
                      {exporting ? '⏳ 만드는 중...' : `📥 ${exportYear}년 연간 엑셀 다운로드`}
                    </button>
                  </div>
                )}
                <button onClick={() => setShowExport(false)}
                  style={{ width:'100%', padding:'7px', background:'none', border:'1px solid #ddd',
                    borderRadius:8, fontSize:12, color:'#888', cursor:'pointer', marginTop:10 }}>닫기</button>
              </div>
            )}
          </div>
          {canWriteSchedules && (
            <button onClick={() => { setShowForm(true); setEditData(null); setFormDate(''); }}
              style={{ background:'#185FA5', color:'#fff', border:'none', borderRadius:8,
                padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
              + 일정 등록
            </button>
          )}
        </div>
      </div>

      {msg && <div style={{ background:'#d1fae5', color:'#065f46', padding:'10px 16px', borderRadius:8, marginBottom:16, fontSize:13 }}>{msg}</div>}

      {/* 월 이동 */}
      <div ref={datePickerRef} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, marginBottom:16, flexWrap:'wrap', position:'relative' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          <button onClick={() => { if(curMonth===0){setCurYear(y=>y-1);setCurMonth(11);}else setCurMonth(m=>m-1); }}
            style={{ background:'none', border:'1px solid #ddd', borderRadius:6, padding:'6px 12px', cursor:'pointer', fontSize:14 }}>◀</button>
          <span onClick={() => setShowDatePicker(v=>!v)}
            style={{ fontSize:18, fontWeight:700, color:'#000', minWidth:120, textAlign:'center', cursor:'pointer' }}>
            {curYear}년 {curMonth+1}월
          </span>
          <button onClick={() => { if(curMonth===11){setCurYear(y=>y+1);setCurMonth(0);}else setCurMonth(m=>m+1); }}
            style={{ background:'none', border:'1px solid #ddd', borderRadius:6, padding:'6px 12px', cursor:'pointer', fontSize:14 }}>▶</button>
          <button onClick={() => { setCurYear(new Date().getFullYear()); setCurMonth(new Date().getMonth()); }}
            style={{ background:'#f0f0f0', border:'none', borderRadius:6, padding:'6px 12px', cursor:'pointer', fontSize:13, color:'#555' }}>오늘</button>
        </div>

        {/* 2026-09 요청: 범주(회의/업무/발표...) 범례를 이 줄 우측 끝으로 이동. 클릭하면
            해당 범주만 필터링해서 보여주고, 맨 우측 "전체보기"를 누르면 필터를 해제한다. */}
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          {CAT_LIST.map(cat => {
            const active = categoryFilter===cat;
            return (
              <div key={cat} onClick={() => setCategoryFilter(active ? null : cat)}
                style={{ display:'flex', alignItems:'center', gap:5, fontSize:14,
                  color: active ? CAT_COLOR[cat] : '#555', cursor:'pointer',
                  padding:'3px 8px', borderRadius:12,
                  background: active ? CAT_BG[cat] : 'transparent',
                  border: active ? `1px solid ${CAT_COLOR[cat]}` : '1px solid transparent',
                  fontWeight: active ? 700 : 400 }}>
                <div style={{ width:10, height:10, borderRadius:3, background:CAT_COLOR[cat], flexShrink:0 }}></div>
                {cat}
              </div>
            );
          })}
          <button onClick={() => setCategoryFilter(null)}
            style={{ background: categoryFilter ? '#185FA5' : '#f0f0f0', color: categoryFilter ? '#fff' : '#888',
              border:'none', borderRadius:6, padding:'5px 12px', cursor:'pointer', fontSize:12, fontWeight:600, marginLeft:4 }}>
            전체보기
          </button>
        </div>

        {showDatePicker && (
          <div style={{ position:'absolute', top:38, left:0, background:'#fff', border:'1px solid #ddd',
            borderRadius:10, boxShadow:'0 4px 16px rgba(0,0,0,0.12)', padding:12, zIndex:50, width:220 }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
              <button onClick={() => setPickerYear(y=>y-1)}
                style={{ background:'none', border:'none', fontSize:13, color:'#888', cursor:'pointer', padding:'2px 4px' }}>◀</button>
              <span style={{ fontWeight:600, fontSize:14 }}>{pickerYear}년</span>
              <button onClick={() => setPickerYear(y=>y+1)}
                style={{ background:'none', border:'none', fontSize:13, color:'#888', cursor:'pointer', padding:'2px 4px' }}>▶</button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:6 }}>
              {Array.from({length:12}, (_,i)=>i+1).map(m => {
                const active = pickerYear===curYear && m===curMonth+1;
                return (
                  <button key={m}
                    onClick={() => { setCurYear(pickerYear); setCurMonth(m-1); setShowDatePicker(false); }}
                    style={{ padding:'6px 0', borderRadius:6, border:'1px solid',
                      borderColor: active?'#185FA5':'#ddd',
                      background:  active?'#E6F1FB':'#fff',
                      color:       active?'#185FA5':'#555',
                      fontSize:14, cursor:'pointer' }}>
                    {m}월
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* 캘린더 뷰 */}
      {viewMode==='calendar' && (
        <div>
          <div style={{ background:'#fff', borderRadius:12, border:'1px solid #e5e7eb', overflowX:'auto' }}>
            {/* 요일 헤더 — 요청사항: 일정 제목이 길어도 요일별 칸 폭이 내용에 따라 제멋대로
                늘어나지 않도록, 그리드 트랙 최소폭을 0으로 강제해(minmax(0,1fr)) 항상 전체
                가로폭을 7등분한 고정 폭을 유지한다(기본 1fr은 트랙의 자동 최소폭이 내용의
                min-content 크기를 따라가 버려서, 긴 제목이 있는 요일 칸만 넓어지는 문제가 있었음). */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7,minmax(0,1fr))' }}>
              {WEEKDAY.map((d,i) => (
                <div key={d} style={{ padding:'10px 0', textAlign:'center', fontSize:13, fontWeight:600,
                  color:i===0?'#dc2626':i===6?'#185FA5':'#444',
                  background:'#f8fafc', borderBottom:'1px solid #e5e7eb' }}>{d}</div>
              ))}
            </div>
            {/* 날짜 */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7,minmax(0,1fr))' }}>
              {cells.map((day, idx) => {
                if (!day) return (
                  <div key={'e'+idx} style={{ minHeight:110, minWidth:0, overflow:'hidden', background:'#fafafa',
                    borderRight:'1px solid #f0f0f0', borderBottom:'1px solid #f0f0f0' }} />
                );
                const dateStr   = curYear+'-'+String(curMonth+1).padStart(2,'0')+'-'+String(day).padStart(2,'0');
                const holidayNames = holidays[dateStr]; // 공휴일이면 ['대체공휴일(광복절)', ...], 아니면 undefined
                const dayScheds = schedulesOnDate(dateStr);
                // 생산관리에서 자동등록된 일정(source_plan_id 있음)은 아래 일정 목록에 글자로
                // 나열하지 않고, 우측 상단에 아이콘 뱃지 하나로만 표시한다(daySales/dayShipments
                // 뱃지와 동일한 방식). 수동으로 등록한 일반 일정만 목록에 계속 표시됨.
                const dayProdScheds  = dayScheds.filter(s => s.source_plan_id);
                // 여러날 일정의 "중간" 날짜(시작일도 종료일도 아닌 날)는 캘린더에 표시하지 않는다.
                const dayVisibleScheds = dayScheds.filter(s => !s.source_plan_id && !isMidSpanDay(s, dateStr));
                // 연기 이력의 "옛 날짜"들 — 여러 번 연기됐다면 그 각각의 옛 날짜 자리에, 몇 번째
                // 연기였는지(1차/2차/...)를 알 수 있도록 흐린 표시를 남겨둔다. 현재 실제로 잡혀
                // 있는 날짜(dateStr===s.start_date)는 위쪽에서 이미 살아있는 일정으로 표시되므로
                // 여기서는 제외한다.
                const dayGhostScheds = [];
                list.forEach(s => {
                  if (s.source_plan_id || !s.postpone_count) return;
                  historyDates(s).forEach((d, idx) => {
                    if (d === dateStr && d !== s.start_date) dayGhostScheds.push({ s, ordinal: idx+1 });
                  });
                });
                const isToday   = dateStr===today;
                const dow       = new Date(dateStr).getDay();
                const daySales       = salesOnDate(dateStr);
                const dayShipments   = shipmentsOnDate(dateStr);
                const dayProductions = productionsOnDate(dateStr);

                // 일정이 여러 개일 때, 잘려서 안 보이는 대신 글자 크기를 1px씩 줄여가며 최대한 다 보여줌
                const shrinkFont = Math.max(7, 13 - Math.max(0, dayVisibleScheds.length - 2));
                const shrinkPad  = dayVisibleScheds.length > 4 ? '1px 3px' : '2px 6px';
                const visibleScheds = dayVisibleScheds.slice(0, 8); // 극단적으로 많을 때를 위한 최후 안전판
                const extraCount    = dayVisibleScheds.length - visibleScheds.length;

                // 날짜 칸 클릭: 등록된 일정(영업/출고/생산 포함)이 있으면 통합 목록을, 없으면(관리자만) 바로 등록폼을 띔
                const hasAnyItem = dayScheds.length>0 || daySales.length>0 || dayShipments.length>0 || dayProductions.length>0;
                const handleDayClick = () => {
                  if (hasAnyItem) {
                    const items = [
                      ...dayScheds.map(s => ({ type:'schedule', key:'s'+s.id, data:s })),
                      ...daySales.map(s => ({ type:'sales', key:'a'+s.id, data:s })),
                      ...dayShipments.map(s => ({ type:'shipment', key:'h'+s.id, data:s })),
                      ...dayProductions.map(p => ({ type:'production', key:'p'+p.id, data:p })),
                    ];
                    setDayList({ dateStr, items });
                  } else if (canWriteSchedules) {
                    setFormDate(dateStr); setEditData(null); setShowForm(true);
                  }
                };

                return (
                  <div key={dateStr}
                    style={{ minHeight:110, minWidth:0, padding:6, borderRight:'1px solid #f0f0f0', borderBottom:'1px solid #f0f0f0',
                      overflow:'hidden', background:isToday?'#EFF6FF':'#fff', cursor:(hasAnyItem||canWriteSchedules)?'pointer':'default' }}
                    onClick={handleDayClick}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:3, minWidth:0, overflow:'hidden' }}>
                        <div style={{ fontSize:13, width:22, height:22, borderRadius:'50%', flexShrink:0,
                          background:isToday?'#185FA5':'transparent',
                          color:isToday?'#fff':holidayNames?'#dc2626':dow===0?'#dc2626':dow===6?'#2563eb':'#333',
                          display:'flex', alignItems:'center', justifyContent:'center',
                          fontWeight:isToday?700:400 }}>{day}</div>
                        {holidayNames && holidayNames.length>0 && (
                          <span title={holidayNames.join(', ')}
                            style={{ fontSize:11, color:'#dc2626', fontWeight:600,
                              whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                            ({holidayNames[0]})
                          </span>
                        )}
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
                        {daySales.length > 0 && (
                          <span title={`영업활동 ${daySales.length}건`}
                            onClick={e => { e.stopPropagation();
                              if (daySales.length === 1) setSalesDetailId(daySales[0].id);
                              else setDayPicker({ dateStr, sales:daySales, shipments:[] }); }}
                            style={{ fontSize:11, padding:'1px 5px', borderRadius:8, background:'#EDE9FE', color:'#7C3AED',
                              fontWeight:700, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:2 }}>
                            💼{daySales.length>1 ? daySales.length : ''}
                          </span>
                        )}
                        {dayShipments.length > 0 && (
                          <span title={`출고 ${dayShipments.length}건`}
                            onClick={e => { e.stopPropagation();
                              if (dayShipments.length === 1) setShipmentDetail(dayShipments[0]);
                              else setDayPicker({ dateStr, sales:[], shipments:dayShipments }); }}
                            style={{ fontSize:11, padding:'1px 5px', borderRadius:8, background:'#E6F1FB', color:'#185FA5',
                              fontWeight:700, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:2 }}>
                            🚚{dayShipments.length>1 ? dayShipments.length : ''}
                          </span>
                        )}
                        {dayProdScheds.length > 0 && (
                          <span
                            title={dayProdScheds.length === 1 ? dayProdScheds[0].title : `생산 관련 일정 ${dayProdScheds.length}건`}
                            onClick={e => {
                              e.stopPropagation();
                              if (dayProdScheds.length === 1) setSelected(dayProdScheds[0]);
                              else setDayList({ dateStr, items: dayProdScheds.map(s => ({ type:'schedule', key:'s'+s.id, data:s })) });
                            }}
                            style={{ fontSize:11, padding:'1px 5px', borderRadius:8, background:'#EDE9FE', color:'#5B21B6',
                              fontWeight:700, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:2 }}>
                            {dayProdScheds.length === 1 ? (PROD_EVENT_ICON[dayProdScheds[0].production_event] || '🏭') : '🏭'}
                            {dayProdScheds.length>1 ? dayProdScheds.length : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    {visibleScheds.map(s => (
                      <div key={s.id}
                        onClick={e => { e.stopPropagation(); setSelected(s); }}
                        style={{ fontSize:shrinkFont, padding:shrinkPad, borderRadius:4, marginBottom:1,
                          background: CAT_COLOR[s.category]||'#185FA5',
                          color:'#fff', fontWeight:400,
                          whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', cursor:'pointer',
                          opacity: isRestricted(s) ? 0.7 : 1, lineHeight:1.3 }}>
                        {s.completed ? '✓ ' : ''}{isRestricted(s) && '🔒 '}{prodIcon(s)}{s.title}
                        {/* 시작일 칸에는 "(종료일 M/D)", 종료일 칸에는 "(완료)"를 덧붙인다. */}
                        {spanLabel(s, dateStr)}
                        {/* D+xx — 연기된 일정이 현재 실제로 잡혀있는 날(시작일)에만, 딱 한 번 표시.
                            같은 일정이 여러 날에 걸쳐 있어도 나머지 날에는 표시하지 않는다. */}
                        {s.postpone_count>0 && dateStr===s.start_date && ` (D+${postponeDays(s)})`}
                      </div>
                    ))}
                    {/* 지연 표시 — 연기로 인해 이제는 더 이상 실제 일정이 없는 "옛 날짜" 자리에,
                        몇 차 연기였는지와 함께 흐린 표시를 남겨둔다(클릭하면 옮겨간 최신 일정
                        상세를 그대로 보여준다). */}
                    {dayGhostScheds.map(({ s, ordinal }) => (
                      <div key={'ghost'+s.id+'-'+ordinal}
                        onClick={e => { e.stopPropagation(); setSelected(s); }}
                        style={{ fontSize:shrinkFont, padding:shrinkPad, borderRadius:4, marginBottom:1,
                          background:'#fff', border:'1px dashed #bbb', color:'#999', fontWeight:400,
                          whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', cursor:'pointer',
                          lineHeight:1.3 }}>
                        {prodIcon(s)}{s.title} ({postponeLabel(s, ordinal)})
                      </div>
                    ))}
                    {extraCount>0 && <div style={{ fontSize:10, color:'#888' }}>+{extraCount}개</div>}
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      )}

      {/* 목록 뷰 */}
      {viewMode==='list' && (
        <div style={{ background:'#fff', borderRadius:12, border:'1px solid #e5e7eb', overflowX:'auto' }}>
          {thisMonthList.length===0 ? (
            <div style={{ textAlign:'center', padding:'60px 0', color:'#aaa', fontSize:14 }}>이번 달 일정이 없습니다.</div>
          ) : (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'80px 1fr 110px 110px 110px 90px',
                background:'#f8fafc', padding:'10px 16px', fontSize:12, fontWeight:600,
                color:'#666', borderBottom:'1px solid #e5e7eb' }}>
                <span>구분</span><span>제목</span><span>등록자</span><span>시작일</span><span>종료일</span>
                <span style={{textAlign:'center'}}>공개구분</span>
              </div>
              {thisMonthList.map((s, i) => (
                <div key={s.id} onClick={() => setSelected(s)}
                  style={{ display:'grid', gridTemplateColumns:'80px 1fr 110px 110px 110px 90px',
                    padding:'12px 16px', fontSize:13,
                    borderBottom:i<thisMonthList.length-1?'1px solid #f0f0f0':'none',
                    cursor:'pointer', alignItems:'center' }}
                  onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'}
                  onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
                  <span>
                    <span style={{ fontSize:11, padding:'3px 8px', borderRadius:20, fontWeight:500,
                      background:CAT_BG[s.category]||'#f0f0f0',
                      color:CAT_COLOR[s.category]||'#555' }}>{s.category}</span>
                  </span>
                  <span>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <span style={{ width:10, height:10, borderRadius:'50%',
                        background:CAT_COLOR[s.category]||'#185FA5', flexShrink:0 }}></span>
                      <span style={{ fontWeight:500 }}>
                        {s.completed ? '✓ ' : ''}{prodIcon(s)}{s.title}
                        {s.postpone_count>0 && ` (D+${postponeDays(s)})`}
                      </span>
                    </div>
                  </span>
                  <span style={{ fontSize:12, color:'#666' }}>
                    {[s.created_by_position, s.created_by].filter(Boolean).join(' ') || '-'}
                  </span>
                  <span style={{ fontSize:12, color:'#666' }}>{s.start_date}</span>
                  <span style={{ fontSize:12, color:'#666' }}>{s.end_date||s.start_date}</span>
                  <span style={{ textAlign:'center' }} title={s.visibility || ''}>
                    {(s.visibility === '전체공개' || (!s.visibility && s.is_public === 1))
                      ? <span style={{ fontSize:11, padding:'2px 6px', borderRadius:10, background:'#E1F5EE', color:'#0F6E56', fontWeight:500 }}>🌐 전체</span>
                      : <span style={{ fontSize:11, padding:'2px 6px', borderRadius:10, background:'#FBEAF0', color:'#993556', fontWeight:500 }}>🔒 제한</span>
                    }
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* 상세 모달 */}
      {selected && (
        <SchedDetail
          sched={selected}
          isAdmin={canWriteSchedules}
          onClose={() => setSelected(null)}
          onEdit={s => { setEditData(s); setFormDate(''); setShowForm(true); setSelected(null); }}
          onDelete={handleDelete}
          onChanged={load}
        />
      )}

      {/* 등록/수정 폼 */}
      {showForm && (
        <SchedForm
          editData={editData}
          defaultDate={formDate}
          onClose={() => { setShowForm(false); setEditData(null); setFormDate(''); }}
          onSaved={() => {
            setShowForm(false); setEditData(null); setFormDate('');
            load(); setMsg(editData?'수정 완료!':'등록 완료!');
          }}
        />
      )}

      {/* 하루에 여러 건일 때 선택 팝업 (영업활동/출고) */}
      {dayPicker && (
        <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }} onClick={() => setDayPicker(null)}>
          <div style={{ background:'#fff', borderRadius:16, padding:24, width:380, maxWidth:'90vw', maxHeight:'80vh', overflowY:'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
              <h3 style={{ fontSize:15, fontWeight:600, margin:0 }}>
                {dayPicker.sales.length > 0 ? '영업활동 상세' : dayPicker.shipments.length > 0 ? '출고 상세' : `${dayPicker.dateStr} 내역`}
              </h3>
              <button onClick={() => setDayPicker(null)} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
            </div>
            {dayPicker.sales.map((s,i) => (
              <div key={'s'+i} onClick={() => { setSalesDetailId(s.id); setDayPicker(null); }}
                style={{ padding:'10px 12px', background:'#EDE9FE', borderRadius:8, marginBottom:6, cursor:'pointer' }}>
                <div style={{ fontSize:13, display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ color:'#7C3AED', fontWeight:600, flexShrink:0 }}>💼 {s.time}</span>
                  <span style={{ color:'#555' }}>{s.partner} → {s.customer}</span>
                </div>
              </div>
            ))}
            {dayPicker.shipments.map((s,i) => (
              <div key={'h'+i} onClick={() => { setShipmentDetail(s); setDayPicker(null); }}
                style={{ padding:'10px 12px', background:'#E6F1FB', borderRadius:8, marginBottom:6, cursor:'pointer' }}>
                <div style={{ fontSize:13, display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ color:'#185FA5', fontWeight:600, flexShrink:0 }}>🚚 {s.customer}</span>
                  <span style={{ color:'#555' }}>{s.model_no||'-'}{s.color ? ' · '+s.color : ''}{s.quantity ? ' · '+s.quantity+'개' : ''}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 영업관리 상세 모달 (재사용) */}
      {salesDetailId && (
        <SalesDetailModal
          id={salesDetailId}
          title="영업관리 상세"
          isSuperAdmin={isSuperAdmin}
          user={user}
          onClose={() => setSalesDetailId(null)}
          onChanged={() => loadSales()}
          onEdit={() => { window.location.href = '/sales-activities'; }}
          onDelete={async (id) => {
            if (!window.confirm('정말 삭제하시겠습니까? (최고관리자만 가능)')) return;
            try { await API.delete('/sales-activities/'+id); setSalesDetailId(null); loadSales(); }
            catch(err) { alert(err.response?.data?.message || '오류'); }
          }}
        />
      )}

      {/* 출고 상세 모달 (간단 보기) */}
      {shipmentDetail && (
        <ShipmentDetailModal shipment={shipmentDetail} onClose={() => setShipmentDetail(null)} />
      )}

      {/* 생산계획 상세 모달 (간단 보기) */}
      {productionDetail && (
        <ProductionDetailModal plan={productionDetail} onClose={() => setProductionDetail(null)} />
      )}

      {/* 날짜 칸 클릭: 그날 등록된 일정/영업/출고/생산 통합 목록 */}
      {dayList && (
        <DayListModal
          dateStr={dayList.dateStr}
          items={dayList.items}
          isAdmin={canWriteSchedules}
          onOpen={(item) => {
            setDayList(null);
            if (item.type === 'schedule')   setSelected(item.data);
            if (item.type === 'sales')       setSalesDetailId(item.data.id);
            if (item.type === 'shipment')    setShipmentDetail(item.data);
            if (item.type === 'production')  setProductionDetail(item.data);
          }}
          onAdd={() => { setDayList(null); setFormDate(dayList.dateStr); setEditData(null); setShowForm(true); }}
          onClose={() => setDayList(null)}
        />
      )}
    </div>
  );
}

// ── 출고 상세 보기 모달 (읽기 전용) ─────────────────
function ShipmentDetailModal({ shipment: s, onClose }) {
  const rowStyle = { display:'flex', gap:10, fontSize:14, marginBottom:8 };
  const labelStyle = { width:80, color:'#888', flexShrink:0 };
  const rows = [
    ['출고일자', s.ship_date],
    ['구분', s.type],
    ['고객사', [s.customer, s.customer_department].filter(Boolean).join(' ')],
    ['담당자', [s.manager, s.position].filter(Boolean).join(' ')],
    ['연락처', s.phone],
    ['제품명', s.model_no],
    ['색상', s.color],
    ['수량', s.quantity],
    ['공급단가', s.price],
    ['결제방식', s.pay_method],
    ['출고방법', s.delivery_method],
    ['출고여부', s.shipped_status],
    ['비고', s.note],
  ].filter(([,v]) => v);

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:420, maxWidth:'92vw', maxHeight:'86vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0 }}>🚚 출고 상세</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>
        <div style={{ background:'#f8fafc', borderRadius:10, padding:'14px 16px' }}>
          {rows.map(([label,val]) => (
            <div key={label} style={rowStyle}><span style={labelStyle}>{label}</span><span>{val}</span></div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── 생산계획 상세 보기 모달 (읽기 전용) ─────────────────
function ProductionDetailModal({ plan: p, onClose }) {
  const rowStyle = { display:'flex', gap:10, fontSize:14, marginBottom:8 };
  const labelStyle = { width:80, color:'#888', flexShrink:0 };
  const rows = [
    ['구분', p.category],
    ['모델명', p.model_no],
    ['색상', p.colors],
    ['계획수량', p.plan_qty],
    ['담당자', p.manager],
    ['시작일', p.start_date],
    ['종료일', p.end_date],
    ['비고', p.note],
  ].filter(([,v]) => v);

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:420, maxWidth:'92vw', maxHeight:'86vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0 }}>🏭 생산계획 상세</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>
        <div style={{ background:'#f8fafc', borderRadius:10, padding:'14px 16px' }}>
          {rows.map(([label,val]) => (
            <div key={label} style={rowStyle}><span style={labelStyle}>{label}</span><span>{val}</span></div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── 날짜 칸 클릭시 통합 목록 모달 (일정/영업/출고/생산) ─────
function DayListModal({ dateStr, items, isAdmin, onOpen, onAdd, onClose }) {
  const ICON  = { schedule:'📅', sales:'💼', shipment:'🚚', production:'🏭' };
  const BG    = { schedule:'#E6F1FB', sales:'#EDE9FE', shipment:'#E6F1FB', production:'#EDE9FE' };
  const COLOR = { schedule:'#185FA5', sales:'#7C3AED', shipment:'#185FA5', production:'#5B21B6' };
  const labelOf = (item) => {
    const d = item.data;
    if (item.type === 'schedule')  return prodIcon(d) + d.title;
    if (item.type === 'sales')     return `${d.partner||'-'} → ${d.customer||'-'}`;
    if (item.type === 'shipment')  return `${d.customer||'-'}${d.model_no ? ' · '+d.model_no : ''}`;
    if (item.type === 'production') return `${d.model_no || d.category || '생산계획'}`;
    return '-';
  };
  const typeLabel = { schedule:'일정', sales:'영업활동', shipment:'출고', production:'생산' };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:1500 }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:24, width:420, maxWidth:'90vw', maxHeight:'80vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <h3 style={{ fontSize:15, fontWeight:600, margin:0 }}>{dateStr} 일정</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        {items.length === 0 ? (
          <div style={{ fontSize:13, color:'#aaa', textAlign:'center', padding:'20px 0' }}>등록된 내용이 없습니다.</div>
        ) : (
          items.map(item => (
            <div key={item.key} onClick={() => onOpen(item)}
              style={{ padding:'10px 12px', background:BG[item.type], borderRadius:8, marginBottom:6, cursor:'pointer' }}>
              <div style={{ fontSize:13, display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ color:COLOR[item.type], fontWeight:600, flexShrink:0 }}>{ICON[item.type]} {typeLabel[item.type]}</span>
                <span style={{ color:'#555', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{labelOf(item)}</span>
              </div>
            </div>
          ))
        )}

        {isAdmin && (
          <button onClick={onAdd}
            style={{ width:'100%', padding:'11px', background:'#185FA5', color:'#fff', border:'none',
              borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:600, marginTop:10 }}>
            + 일정추가
          </button>
        )}
      </div>
    </div>
  );
}

// ── 상세 모달 ─────────────────────────────────────
function SchedDetail({ sched, isAdmin, onClose, onEdit, onDelete, onChanged }) {
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  // 삭제는 등록한 사람 본인 또는 최고관리자만 가능 (서버에서도 동일하게 검증됨)
  const canDelete = user.role === 'superadmin' || user.role === 'admin' || sched.created_by === user.name;

  // 완료 체크 — 낙관적으로 즉시 반영하고, 성공하면 부모(달력/목록)도 새로고침해서 맞춰준다.
  const [completed, setCompleted] = useState(!!sched.completed);
  const [completedAt, setCompletedAt] = useState(sched.completed_at || '');
  const [completedBy, setCompletedBy] = useState(sched.completed_by || '');
  const [toggling, setToggling] = useState(false);
  const toggleComplete = async () => {
    const next = !completed;
    setToggling(true);
    try {
      await API.patch(`/schedules/${sched.id}/complete`, { completed: next });
      setCompleted(next);
      setCompletedAt(next ? new Date().toLocaleString('ko-KR', { hour12:false }).replace(/\. /g,'-').replace('.','') : '');
      setCompletedBy(next ? (user.name||'') : '');
      onChanged && onChanged();
    } catch (e) {
      alert(e.response?.data?.message || '처리 중 오류가 발생했습니다.');
    } finally {
      setToggling(false);
    }
  };

  // 연기(날짜변경) 이력 — 상세를 열 때 한 번 조회하고, 연기 등록 후 다시 불러온다.
  const [history, setHistory] = useState([]);
  const loadHistory = () => {
    API.get(`/schedules/${sched.id}/history`).then(r => setHistory(r.data || [])).catch(() => {});
  };
  useEffect(() => { loadHistory(); }, [sched.id]);
  const deleteHistory = (h) => {
    if (!window.confirm('이 연기 이력을 삭제할까요?')) return;
    API.delete(`/schedules/${sched.id}/history/${h.id}`)
      .then(() => { loadHistory(); onChanged && onChanged(); })
      .catch(e => alert(e.response?.data?.message || '이력 삭제 중 오류가 발생했습니다.'));
  };

  // 연기 등록 모달 — "연기" 버튼을 누르면 새 날짜/사유를 입력받는다. 등록 후에는
  // 화면에 즉시 반영되도록 시작일/종료일을 로컬 상태로 따로 들고 있는다.
  const [curStart, setCurStart] = useState(sched.start_date);
  const [curEnd, setCurEnd] = useState(sched.end_date);
  const [showPostpone, setShowPostpone] = useState(false);

  // 일정알림 — 상세화면에서도 등록/수정 모달과 동일하게 바로 알림을 보낼 수 있다.
  // 참석자알림 대상 수는 이 일정에 저장된 attendees(JSON 문자열)를 파싱해서 계산한다.
  const [showNotify, setShowNotify] = useState(false);
  const attendeeCount = (() => {
    if (!sched.attendees) return 0;
    try { const v = JSON.parse(sched.attendees); return Array.isArray(v) ? v.length : 0; } catch (e) { return 0; }
  })();

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:480,
        maxWidth:'92vw', maxHeight:'88vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
          <div>
            <div style={{ display:'flex', gap:6, marginBottom:8, alignItems:'center', flexWrap:'wrap' }}>
              <span style={{ fontSize:11, padding:'3px 8px', borderRadius:20, fontWeight:500,
                background:CAT_BG[sched.category]||'#f0f0f0',
                color:CAT_COLOR[sched.category]||'#555' }}>{sched.category}</span>
              <span style={{ width:12, height:12, borderRadius:'50%',
                background:CAT_COLOR[sched.category]||'#185FA5', display:'inline-block' }}></span>
              {/* 공개범위 뱃지 — 계정관리 > 공개범위 설정에서 만든 공개구분(visibility)을 그대로 표시.
                  예전에는 is_public(0/1) 값만으로 "관리자만 공개/전체 공개" 둘 중 하나로만 표시해서,
                  "사내전체(생산, 품질 제외)" 같은 커스텀 공개구분을 선택해도 실제 설정과 다르게
                  "관리자만 공개"라고 잘못 표시되는 문제가 있었음. */}
              {sched.visibility === '전체공개' || (!sched.visibility && sched.is_public === 1)
                ? <span style={{ fontSize:11, padding:'3px 8px', borderRadius:20,
                    background:'#E1F5EE', color:'#0F6E56', fontWeight:500 }}>🌐 {sched.visibility || '전체 공개'}</span>
                : <span style={{ fontSize:11, padding:'3px 8px', borderRadius:20,
                    background:'#FBEAF0', color:'#993556', fontWeight:500 }}>🔒 {sched.visibility || '관리자만 공개'}</span>
              }
              {completed && (
                <span style={{ fontSize:11, padding:'3px 8px', borderRadius:20,
                  background:'#DCFCE7', color:'#15803D', fontWeight:600 }}>✓ 완료</span>
              )}
            </div>
            <h3 style={{ fontSize:18, fontWeight:600, margin:'0 0 4px', color:'#111' }}>
              {completed && <span style={{ color:'#15803D' }}>✓ </span>}{prodIcon(sched)}{sched.title}
            </h3>
            {completed && completedAt && (
              <div style={{ fontSize:11, color:'#15803D' }}>{completedBy} · {completedAt} 완료</div>
            )}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
            {/* 일정알림 — 삭제 버튼 왼쪽에 배치. 등록/수정 모달 헤더의 버튼과 동일한 스타일. */}
            <button type="button" onClick={() => setShowNotify(true)}
              style={{ background:'#FFF7E6', border:'1px solid #FBBF24', color:'#B45309', borderRadius:8,
                padding:'6px 10px', fontSize:12.5, fontWeight:600, cursor:'pointer' }}>
              🔔 일정알림
            </button>
            {/* 삭제 — 예전에는 아이콘만 있던 버튼을 글자가 보이는 버튼으로 변경 */}
            {canDelete && (
              <button onClick={() => onDelete(sched.id)}
                style={{ background:'#FEF2F2', border:'1px solid #FCA5A5', color:'#dc2626', borderRadius:8,
                  padding:'6px 10px', fontSize:12.5, fontWeight:600, cursor:'pointer' }}>
                🗑 삭제
              </button>
            )}
            <button onClick={onClose}
              style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
          </div>
        </div>

        <div style={{ background:'#f8fafc', borderRadius:10, padding:'14px 16px', marginBottom:14 }}>
          {[['장소',sched.location],
            ['시간', sched.start_time ? (sched.end_time ? `${sched.start_time} ~ ${sched.end_time}` : sched.start_time) : null],
            ['시작일',curStart],
            ['종료일',curEnd&&curEnd!==curStart?curEnd:null]].map(([label,val])=>val?(
            <div key={label} style={{ display:'flex', gap:12, marginBottom:8, fontSize:14 }}>
              <span style={{ width:50, color:'#888', flexShrink:0 }}>{label}</span>
              <span style={{ fontWeight:500 }}>{val}</span>
            </div>
          ):null)}
        </div>

        {sched.content && (
          <div style={{ padding:'12px 14px', background:'#f0f6ff', borderRadius:8,
            fontSize:13, color:'#444', lineHeight:1.7, whiteSpace:'pre-wrap', marginBottom:14 }}>
            {sched.content}
          </div>
        )}

        {/* 연기(날짜변경) 이력 — 시작일/종료일을 바꿔 저장할 때마다 자동으로 쌓인다 */}
        {history.length > 0 && (
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:12, fontWeight:600, color:'#92400e', marginBottom:6 }}>
              🔁 연기 이력 {history.length}건
            </div>
            <div style={{ background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:8, padding:'8px 12px' }}>
              {history.map((h, i) => (
                <div key={h.id} style={{ fontSize:12, color:'#78350F', padding:'5px 0',
                  borderTop: i>0 ? '1px solid #FDE68A' : 'none', display:'flex',
                  justifyContent:'space-between', alignItems:'flex-start', gap:6 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div>
                      <span style={{ textDecoration:'line-through', color:'#a8886a' }}>
                        {h.old_start_date}{h.old_end_date && h.old_end_date!==h.old_start_date ? ` ~ ${h.old_end_date}` : ''}
                      </span>
                      {' → '}
                      <span style={{ fontWeight:600 }}>
                        {h.new_start_date}{h.new_end_date && h.new_end_date!==h.new_start_date ? ` ~ ${h.new_end_date}` : ''}
                      </span>
                    </div>
                    {h.reason && <div style={{ marginTop:2, color:'#92400e' }}>사유: {h.reason}</div>}
                    <div style={{ marginTop:2, fontSize:11, color:'#b08d5f' }}>{h.changed_by} · {h.changed_at?.slice(0,16)}</div>
                  </div>
                  {isAdmin && (
                    <button onClick={() => deleteHistory(h)} title="이 이력 삭제"
                      style={{ background:'none', border:'none', color:'#b45309', cursor:'pointer',
                        fontSize:12, padding:'2px 4px', flexShrink:0 }}>✕</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ borderTop:'1px solid #f0f0f0', paddingTop:12 }}>
          {/* 등록: 부서 | 직급 | 이름 | 등록일 등록시간 순서로 표기 */}
          {!sched.updated_at && (
            <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#aaa', flexWrap:'wrap' }}>
              <span>✍️ 등록:</span>
              {sched.created_by_department && <span style={{ color:'#666' }}>{sched.created_by_department}</span>}
              {sched.created_by_department && <span>|</span>}
              {sched.created_by_position && <span style={{ color:'#666' }}>{sched.created_by_position}</span>}
              {sched.created_by_position && <span>|</span>}
              <span style={{ color:'#666', fontWeight:500 }}>{sched.created_by||'-'}</span>
              <span>|</span>
              <span>{sched.created_at?sched.created_at.slice(0,16):''}</span>
            </div>
          )}
          {sched.updated_at && (
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#aaa', marginBottom:6, flexWrap:'wrap' }}>
                <span>✍️ 등록:</span>
                {sched.created_by_department && <span style={{ color:'#666' }}>{sched.created_by_department}</span>}
                {sched.created_by_department && <span>|</span>}
                {sched.created_by_position && <span style={{ color:'#666' }}>{sched.created_by_position}</span>}
                {sched.created_by_position && <span>|</span>}
                <span style={{ color:'#666', fontWeight:500 }}>{sched.created_by||'-'}</span>
                <span>|</span>
                <span>{sched.created_at?sched.created_at.slice(0,16):''}</span>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:4, fontSize:12,
                background:'#fef3c7', borderRadius:6, padding:'6px 10px', color:'#92400e' }}>
                <span>✏️ 수정:</span>
                <span style={{ fontWeight:600, marginLeft:2 }}>{sched.updated_at}</span>
                <span>·</span>
                <span style={{ fontWeight:600 }}>{sched.updated_by}</span>
              </div>
            </div>
          )}
        </div>

        {isAdmin && (
          <div style={{ display:'flex', gap:8, marginTop:16 }}>
            <button onClick={toggleComplete} disabled={toggling}
              style={{ flex:1, padding:'10px',
                background: completed ? '#f3f4f6' : '#f0fdf4',
                color: completed ? '#6b7280' : '#15803D',
                border: `1px solid ${completed ? '#9ca3af' : '#15803D'}`,
                borderRadius:8, cursor: toggling ? 'default' : 'pointer',
                fontSize:14, fontWeight:500, opacity: toggling ? 0.6 : 1 }}>
              {completed ? '↩️ 완료취소' : '✓ 완료'}
            </button>
            <button onClick={() => onEdit(sched)}
              style={{ flex:1, padding:'10px', background:'#f0f6ff', color:'#185FA5',
                border:'1px solid #185FA5', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:500 }}>
              ✏️ 수정
            </button>
            <button onClick={() => setShowPostpone(true)}
              style={{ flex:1, padding:'10px', background:'#FFFBEB', color:'#92400e',
                border:'1px solid #FDE68A', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:500 }}>
              🔁 연기
            </button>
          </div>
        )}
      </div>

      {showPostpone && (
        <PostponeModal
          sched={sched}
          currentStart={curStart}
          currentEnd={curEnd}
          onClose={() => setShowPostpone(false)}
          onSaved={(newStart, newEnd) => {
            setCurStart(newStart);
            setCurEnd(newEnd);
            setShowPostpone(false);
            loadHistory();
            onChanged && onChanged();
          }}
        />
      )}

      {/* 일정알림 — 상세화면 헤더의 🔔 버튼으로 즉시 알림 전송 (등록/수정 모달과 동일 컴포넌트 재사용) */}
      {showNotify && (
        <ScheduleNotifyModal
          scheduleId={sched.id}
          attendeeCount={attendeeCount}
          onClose={() => setShowNotify(false)}
        />
      )}
    </div>
  );
}

// ── 연기 등록 모달 ────────────────────────────────
// 최초 등록된 일정 대비, 최종적으로 옮겨갈 날짜 하나만 입력받는다(시작일 기준).
// 원래 일정이 여러 날에 걸친 일정이었다면(예: 3일짜리 출장) 그 기간(일수)은 그대로 유지한
// 채 시작일만 옮긴다 — 종료일을 따로 입력받지 않는다.
function PostponeModal({ sched, currentStart, currentEnd, onClose, onSaved }) {
  const durationDays = (() => {
    if (!currentStart) return 0;
    const a = new Date(currentStart);
    const b = new Date(currentEnd || currentStart);
    const diff = Math.round((b - a) / 86400000);
    return isNaN(diff) || diff < 0 ? 0 : diff;
  })();
  const [newStart, setNewStart] = useState(currentStart || '');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!newStart) { alert('연기할 날짜를 입력해주세요.'); return; }
    setSaving(true);
    try {
      const d = new Date(newStart);
      d.setDate(d.getDate() + durationDays);
      const newEnd = d.toISOString().slice(0, 10);
      await API.patch(`/schedules/${sched.id}/postpone`, {
        new_start_date: newStart, new_end_date: newEnd, reason: reason.trim() || null
      });
      onSaved(newStart, newEnd);
    } catch (e) {
      alert(e.response?.data?.message || '연기 처리 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const inp = { width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:8,
    fontSize:14, boxSizing:'border-box', marginBottom:12, outline:'none' };
  const lbl = { fontSize:13, color:'#555', fontWeight:500, display:'block', marginBottom:4 };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:1100 }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:24, width:360, maxWidth:'90vw' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ fontSize:16, fontWeight:600, margin:0 }}>🔁 일정 연기</h3>
          <button onClick={onClose}
            style={{ background:'none', border:'none', fontSize:18, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        <label style={lbl}>연기할 날짜 *</label>
        <input type="date" value={newStart} onChange={e => setNewStart(e.target.value)} style={inp} />
        {durationDays > 0 && (
          <div style={{ fontSize:12, color:'#888', marginTop:-8, marginBottom:12 }}>
            {durationDays+1}일짜리 일정이라, 종료일은 자동으로 {durationDays}일 뒤로 맞춰집니다.
          </div>
        )}

        <label style={lbl}>연기 사유 (선택)</label>
        <input value={reason} onChange={e => setReason(e.target.value)}
          placeholder="예: 담당자 일정 조율로 인해 연기" style={inp} />

        <button onClick={handleSubmit} disabled={saving}
          style={{ width:'100%', padding:'12px', background:saving?'#aaa':'#185FA5', color:'#fff',
            border:'none', borderRadius:8, cursor:saving?'default':'pointer', fontSize:15, fontWeight:600 }}>
          {saving ? '처리 중...' : '연기 등록'}
        </button>
      </div>
    </div>
  );
}

// ── 등록/수정 폼 ──────────────────────────────────
// 참석자 선택 — 조직도(계정) 정보를 부서별로 묶어서 드롭다운으로 보여주고, 부서 단위
// 전체선택도 가능한 다중선택 컴포넌트. 데이터는 사내 메신저가 쓰는 대화상대 목록 API
// (GET /messages/members — 승인된 회원 전체, 로그인만 되어 있으면 누구나 조회 가능)를
// 그대로 재사용한다(전용 조직도 API는 관리자 전용이라 이 화면에서 쓸 수 없음).
function AttendeeSelect({ value, onChange }) {
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    API.get('/messages/members').then(r => setMembers(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const onDocClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const grouped = {};
  members.forEach(m => {
    const dept = m.department || '미배정';
    (grouped[dept] = grouped[dept] || []).push(m);
  });

  const isSelected = (username) => value.some(v => v.username === username);
  const toggleOne = (m) => {
    if (isSelected(m.username)) onChange(value.filter(v => v.username !== m.username));
    else onChange([...value, { username: m.username, name: m.name, department: m.department, position: m.position }]);
  };
  const isDeptAllSelected = (dept) => grouped[dept].length > 0 && grouped[dept].every(m => isSelected(m.username));
  const toggleDept = (dept) => {
    if (isDeptAllSelected(dept)) {
      onChange(value.filter(v => !grouped[dept].some(m => m.username === v.username)));
    } else {
      const toAdd = grouped[dept].filter(m => !isSelected(m.username))
        .map(m => ({ username: m.username, name: m.name, department: m.department, position: m.position }));
      onChange([...value, ...toAdd]);
    }
  };

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8,
          fontSize: 14, background: '#fff', textAlign: 'left', cursor: 'pointer',
          color: value.length ? '#111' : '#999', boxSizing: 'border-box' }}>
        {value.length ? `참석자 ${value.length}명 선택됨` : '참석자 선택'} ▾
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20,
          background: '#fff', border: '1px solid #ddd', borderRadius: 8, maxHeight: 220, overflowY: 'auto',
          boxShadow: '0 4px 16px rgba(0,0,0,0.14)', padding: 6 }}>
          {Object.keys(grouped).length === 0 && (
            <div style={{ padding: 10, fontSize: 12, color: '#999' }}>불러올 직원이 없습니다.</div>
          )}
          {Object.keys(grouped).map(dept => (
            dept === '미배정' ? (
              // 부서 미배정 인원은 "미배정 전체" 그룹으로 묶지 않고, 직급·이름만 그대로 나열한다.
              <div key={dept} style={{ marginBottom: 4 }}>
                {grouped[dept].map(m => (
                  <label key={m.username} style={{ display: 'flex', alignItems: 'center', gap: 6,
                    padding: '4px 6px', fontSize: 12.5, color: '#333', cursor: 'pointer' }}>
                    <input type="checkbox" checked={isSelected(m.username)} onChange={() => toggleOne(m)} />
                    {m.position || '-'} · {m.name}
                  </label>
                ))}
              </div>
            ) : (
              <div key={dept} style={{ marginBottom: 4 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 6px',
                  fontSize: 12.5, fontWeight: 700, color: '#185FA5', cursor: 'pointer' }}>
                  <input type="checkbox" checked={isDeptAllSelected(dept)} onChange={() => toggleDept(dept)} />
                  {dept} 전체
                </label>
                {grouped[dept].map(m => (
                  <label key={m.username} style={{ display: 'flex', alignItems: 'center', gap: 6,
                    padding: '4px 6px 4px 22px', fontSize: 12.5, color: '#333', cursor: 'pointer' }}>
                    <input type="checkbox" checked={isSelected(m.username)} onChange={() => toggleOne(m)} />
                    {m.position || '-'} · {m.name}
                  </label>
                ))}
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
}

// 일정알림 모달 — 등록/수정 폼 헤더의 "🔔 일정알림" 버튼에서 연다. 전체알림/참석자알림
// 버튼을 누르면 backend/routes/schedules.js의 POST /:id/notify가 대상자를 계산해서
// socket.io로 실시간 팝업을 쏘고, 상태표시줄(ActivityFeedBar)에도 함께 남긴다.
function ScheduleNotifyModal({ scheduleId, attendeeCount, onClose }) {
  const [sending, setSending] = useState(false);
  const send = async (mode) => {
    if (mode === 'attendees' && attendeeCount === 0) { alert('참석자가 지정되어 있지 않습니다.'); return; }
    setSending(true);
    try {
      const r = await API.post(`/schedules/${scheduleId}/notify`, { mode });
      alert(r.data.message || '알림을 전송했습니다.');
      onClose();
    } catch (e) {
      alert(e.response?.data?.message || '알림 전송 중 오류가 발생했습니다.');
    } finally {
      setSending(false);
    }
  };
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1600 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 14, width: 340, maxWidth: '90vw', padding: '22px 20px' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>🔔 일정알림</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#999' }}>✕</button>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button disabled={sending} onClick={() => send('all')}
            style={{ flex: 1, padding: '12px 8px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 8,
              fontSize: 14, fontWeight: 600, cursor: sending ? 'not-allowed' : 'pointer', textAlign: 'center',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <span>전체알림</span>
            <span style={{ fontWeight: 400, fontSize: 11.5 }}>(공개범위 내 전직원)</span>
          </button>
          <button disabled={sending} onClick={() => send('attendees')}
            style={{ flex: 1, padding: '12px 8px', background: '#0F766E', color: '#fff', border: 'none', borderRadius: 8,
              fontSize: 14, fontWeight: 600, cursor: sending ? 'not-allowed' : 'pointer', textAlign: 'center',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <span>참석자알림</span>
            <span style={{ fontWeight: 400, fontSize: 11.5 }}>({attendeeCount}명)</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// 2026-09 요청(3차 재수정): 네이티브 <input type="time">는 lang="ko-KR-u-hc-h23"로도 크롬이
// 오전/오후(12시간제) 표시를 강제로 유지해버려 근본적으로 24시간제를 보장할 수 없었다.
// 그래서 겉모양(테두리 박스 + 시계 아이콘)은 기존과 최대한 비슷하게 유지하면서, 내부는
// 오전/오후 개념이 아예 없는 시(0~23)/분(0~59) 두 칸짜리 순수 텍스트 입력으로 직접 구현한다.
// "13"을 입력하면 그대로 13시로 저장되고, 자정 넘어가는 오전/오후 변환이 일어나지 않는다.
function TimeField24({ value, onChange }) {
  const [hh, mm] = String(value || '').split(':');
  const hourRef = useRef(null);
  const minRef = useRef(null);
  // 버그 수정: 두 번째 숫자를 입력해 시(hour) 칸이 꽉 차면 자동으로 분(minute) 칸으로 포커스를
  // 옮기는데, 이 focus() 호출이 hour input의 blur 이벤트를 "동기적으로" 즉시 발생시킨다.
  // 이때 blur 핸들러가 렌더링 시점에 구조분해된(오래된) hh 값을 참조해 방금 입력한 값을
  // 덮어써버리는 것이 "15/16/17을 입력해도 01로 바뀌는" 근본 원인이었다. blur 핸들러에서
  // 항상 e.target.value(현재 DOM에 실제로 찍혀있는 값)를 직접 읽도록 고쳐서 이 경쟁 상태를 없앤다.
  const handleHour = (e) => {
    let v = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
    if (v.length === 2 && parseInt(v, 10) > 23) v = '23';
    onChange(`${v}:${mm || ''}`);
    if (v.length === 2) minRef.current && minRef.current.focus();
  };
  const handleMinute = (e) => {
    let v = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
    if (v.length === 2 && parseInt(v, 10) > 59) v = '59';
    onChange(`${hh || ''}:${v}`);
  };
  const handleBlurHour = (e) => {
    const v = e.target.value;
    if (v) onChange(`${v.padStart(2, '0')}:${mm || ''}`);
  };
  const handleBlurMinute = (e) => {
    const v = e.target.value;
    if (v) onChange(`${hh || ''}:${v.padStart(2, '0')}`);
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', width: '100%', padding: '9px 12px',
      border: '1px solid #ddd', borderRadius: 8, boxSizing: 'border-box', marginBottom: 12 }}>
      <input ref={hourRef} value={hh || ''} onChange={handleHour} onBlur={handleBlurHour}
        placeholder="00" maxLength={2} inputMode="numeric"
        style={{ width: 20, border: 'none', outline: 'none', textAlign: 'center', fontSize: 14, padding: 0 }} />
      <span style={{ fontSize: 14, color: '#555' }}>:</span>
      <input ref={minRef} value={mm || ''} onChange={handleMinute} onBlur={handleBlurMinute}
        placeholder="00" maxLength={2} inputMode="numeric"
        style={{ width: 20, border: 'none', outline: 'none', textAlign: 'center', fontSize: 14, padding: 0 }} />
      <span style={{ marginLeft: 'auto', color: '#888', fontSize: 14 }}>🕐</span>
    </div>
  );
}

function SchedForm({ editData, defaultDate, onClose, onSaved }) {
  // 참석자 — 수정 시에는 backend GET '/'가 attendees 컬럼을 원본 JSON 문자열 그대로 내려주므로
  // 여기서 파싱해서 초기값으로 사용한다. 파싱 실패(빈 값 등)하면 빈 배열로 시작한다.
  const parseAttendees = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try { const v = JSON.parse(raw); return Array.isArray(v) ? v : []; } catch (e) { return []; }
  };
  const [form, setForm] = useState({
    category:   editData?.category   || '회의',
    title:      editData?.title      || '',
    content:    editData?.content    || '',
    location:   editData?.location   || '',
    start_time: editData?.start_time || '',
    end_time:   editData?.end_time   || '',
    start_date: editData?.start_date || defaultDate || '',
    end_date:   editData?.end_date   || defaultDate || '',
    color:      editData?.color      || CAT_COLOR['회의'],
    is_public:  editData ? (editData.is_public === 0 ? false : true) : false,
    visibility: editData?.visibility || '',
    attendees:  parseAttendees(editData?.attendees),
  });
  const [loading, setLoading] = useState(false);

  // 공개범위: 계정관리 - 공개범위 설정에서 만든 공개구분 목록을 그대로 사용
  // (업무관리/공지사항과 동일한 방식 — 드롭다운 하나로 통일, 신규 등록은 기본값으로
  // 지정된 공개구분을 자동 선택)
  const [scopeOptions, setScopeOptions] = useState([]);
  useEffect(() => {
    API.get('/disclosure/list').then(r => {
      const scopes = r.data.scopes || [];
      setScopeOptions(scopes);
      if (!editData) {
        const def = scopes.find(s => s.is_default);
        if (def) setForm(f => (f.visibility ? f : { ...f, visibility: def.name, is_public: def.name === '전체공개' }));
      }
    }).catch(() => {});
  }, []);

  const handleChange = e => {
    const updated = { ...form, [e.target.name]: e.target.value };
    if (e.target.name === 'category') {
      updated.color = CAT_COLOR[e.target.value] || '#185FA5';
    }
    setForm(updated);
  };


  const handleSubmit = async () => {
    if (!form.title)      { alert('제목을 입력해주세요.'); return; }
    if (!form.start_date) { alert('시작일을 입력해주세요.'); return; }
    if (!form.visibility) { alert('공개범위를 선택해주세요.'); return; }
    setLoading(true);
    try {
      // is_public은 옛 방식(공개구분이 없던 시절) 호환용 필드라, 실제로는 공개범위 값이
      // "전체공개"인지로 판단해서 함께 저장한다(업무관리처럼 이제 드롭다운 하나로 선택).
      const body = { ...form, is_public: form.visibility === '전체공개' ? 1 : 0, visibility: form.visibility };
      if (editData) await API.put('/schedules/'+editData.id, body);
      else          await API.post('/schedules', body);
      onSaved();
    } catch(err) { alert(err.response?.data?.message || '오류 발생'); }
    finally { setLoading(false); }
  };

  const inp = { width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:8,
    fontSize:14, boxSizing:'border-box', marginBottom:12, outline:'none' };
  const lbl = { fontSize:13, color:'#555', fontWeight:500, display:'block', marginBottom:4 };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:500,
        maxWidth:'92vw', maxHeight:'90vh', overflowY:'auto' }}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0 }}>{editData?'일정 수정':'일정 등록'}</h3>
          <button onClick={onClose}
            style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        {/* 구분 */}
        <label style={lbl}>구분 (색상 자동 적용)</label>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:6, marginBottom:12 }}>
          {CAT_LIST.map(cat => (
            <button key={cat}
              onClick={() => handleChange({ target:{ name:'category', value:cat } })}
              style={{ padding:'8px 4px', cursor:'pointer', fontSize:12,
                fontWeight:form.category===cat?600:400,
                border:form.category===cat?`2px solid ${CAT_COLOR[cat]}`:'1.5px solid #ddd',
                borderRadius:8, background:form.category===cat?CAT_BG[cat]:'#fff',
                color:form.category===cat?CAT_COLOR[cat]:'#555',
                display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
              <div style={{ width:14, height:14, borderRadius:3, background:CAT_COLOR[cat] }}></div>
              {cat}
            </button>
          ))}
        </div>

        <label style={lbl}>제목 *</label>
        <input name="title" placeholder="일정 제목" value={form.title} onChange={handleChange} style={inp} />

        {/* 장소 칸을 반으로 나누고 우측에 참석자 선택칸을 배치 — 참석자는 조직도(부서/직급/이름)
            정보를 가져와 부서 단위 전체선택도 가능한 다중선택 드롭다운(AttendeeSelect)으로 고른다. */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <div>
            <label style={lbl}>장소</label>
            <input name="location" placeholder="예: 본사 2층 회의실" value={form.location} onChange={handleChange} style={inp} />
          </div>
          <div>
            <label style={lbl}>참석자</label>
            <div style={{ marginBottom:12 }}>
              <AttendeeSelect value={form.attendees} onChange={(next) => setForm(f => ({ ...f, attendees: next }))} />
            </div>
          </div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <div>
            <label style={lbl}>시작</label>
            {/* 2026-09 요청(4차 재수정): lang="ko-KR-u-hc-h23"으로도 크롬이 오전/오후 표시를
                계속 강제해서, 네이티브 time 입력을 완전히 포기하고 겉모양만 흉내낸 커스텀
                컴포넌트(TimeField24)로 교체. 오전/오후 자체가 코드에 없으니 "13"을 입력하면
                그대로 13시로 저장된다. */}
            <TimeField24 value={form.start_time} onChange={(v) => setForm(f => ({ ...f, start_time: v }))} />
          </div>
          <div>
            <label style={lbl}>종료</label>
            <TimeField24 value={form.end_time} onChange={(v) => setForm(f => ({ ...f, end_time: v }))} />
          </div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <div>
            <label style={lbl}>시작일 *</label>
            <input name="start_date" type="date" value={form.start_date} onChange={handleChange} style={inp} />
          </div>
          <div>
            <label style={lbl}>종료일</label>
            <input name="end_date" type="date" value={form.end_date} onChange={handleChange} style={inp} />
          </div>
        </div>

        <label style={lbl}>내용</label>
        <textarea name="content" placeholder="일정 내용을 입력하세요..."
          value={form.content} onChange={handleChange}
          style={{ ...inp, height:80, resize:'vertical' }} />

        {/* 공개범위 — 계정관리 - 공개범위 설정에서 만든 공개구분을 그대로 사용(업무관리와 동일) */}
        <label style={lbl}>공개범위</label>
        <select name="visibility" value={form.visibility} onChange={handleChange} style={inp}>
          <option value="">공개범위를 선택하세요</option>
          {scopeOptions.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
        </select>

        <button onClick={handleSubmit} disabled={loading}
          style={{ width:'100%', padding:'12px', background:loading?'#aaa':'#185FA5', color:'#fff',
            border:'none', borderRadius:8, fontSize:15, fontWeight:600,
            cursor:loading?'not-allowed':'pointer', marginTop:4 }}>
          {loading?'저장 중...':(editData?'수정 완료':'등록 완료')}
        </button>
      </div>
    </div>
  );
}
